import { useCallback, useEffect, useState } from 'react';
import {
  postsalesopsApi,
  type BuyerMessageRecord,
  type CustomerCaseRecord,
  type PostSalesDashboard,
  type PostSalesRecommendation,
  type QualityEventRecord,
  type ReimbursementRecord,
  type RefundRecord,
  type ReturnRecord,
  type SafeTClaimRecord,
} from '../../api/postsalesops';

type Tab = 'dashboard' | 'returns' | 'refunds' | 'safet' | 'messages' | 'cases' | 'reimbursements' | 'quality' | 'recommendations';

function statusClass(s: string) {
  if (s === 'completed' || s === 'processed' || s === 'paid' || s === 'approved' || s === 'resolved') return 'text-emerald-700 bg-emerald-50';
  if (s === 'in_transit' || s === 'in_progress' || s === 'under_review' || s === 'receiving') return 'text-sky-700 bg-sky-50';
  if (s === 'escalated' || s === 'denied' || s === 'breached') return 'text-red-700 bg-red-50';
  if (s === 'pending' || s === 'requested' || s === 'submitted' || s === 'open') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function severityClass(s?: string) {
  if (s === 'critical') return 'text-red-700 bg-red-50';
  if (s === 'high') return 'text-orange-700 bg-orange-50';
  if (s === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function sentimentClass(s?: string) {
  if (s === 'negative' || s === 'frustrated') return 'text-red-700 bg-red-50';
  if (s === 'positive') return 'text-emerald-700 bg-emerald-50';
  return 'text-slate-600 bg-slate-50';
}

export function PostSalesOpsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<PostSalesDashboard | null>(null);
  const [returns, setReturns] = useState<ReturnRecord[]>([]);
  const [refunds, setRefunds] = useState<RefundRecord[]>([]);
  const [safeT, setSafeT] = useState<SafeTClaimRecord[]>([]);
  const [messages, setMessages] = useState<BuyerMessageRecord[]>([]);
  const [cases, setCases] = useState<CustomerCaseRecord[]>([]);
  const [reimbursements, setReimbursements] = useState<ReimbursementRecord[]>([]);
  const [quality, setQuality] = useState<QualityEventRecord[]>([]);
  const [recommendations, setRecommendations] = useState<PostSalesRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, ret, ref, st, msg, cs, rmb, ql, recs] = await Promise.all([
        postsalesopsApi.dashboard(),
        postsalesopsApi.listReturns(),
        postsalesopsApi.listRefunds(),
        postsalesopsApi.listSafeTClaims(),
        postsalesopsApi.listMessages(),
        postsalesopsApi.listCases(),
        postsalesopsApi.listReimbursements(),
        postsalesopsApi.listQuality(),
        postsalesopsApi.listRecommendations(),
      ]);
      setDashboard(dash);
      setReturns(ret);
      setRefunds(ref);
      setSafeT(st);
      setMessages(msg);
      setCases(cs);
      setReimbursements(rmb);
      setQuality(ql);
      setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load post-sales data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try {
      await postsalesopsApi.generateRecommendations();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Returns Dashboard' },
    { id: 'returns', label: 'Return Center' },
    { id: 'refunds', label: 'Refund Center' },
    { id: 'safet', label: 'SAFE-T Center' },
    { id: 'messages', label: 'Customer Service' },
    { id: 'cases', label: 'Case Manager' },
    { id: 'reimbursements', label: 'Reimbursements' },
    { id: 'quality', label: 'Quality Intelligence' },
    { id: 'recommendations', label: 'AI Recommendations' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Returns, SAFE-T &amp; Post-Sales Intelligence</h1>
          <p className="text-sm text-slate-500">
            Returns, refunds, SAFE-T claims, customer service &amp; quality intelligence — APP-00004
          </p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Open Returns', value: dashboard.open_returns.toString() },
              { label: 'Pending Refunds', value: dashboard.pending_refunds.toString() },
              { label: 'Active SAFE-T', value: dashboard.active_safe_t_claims.toString() },
              { label: 'Open Cases', value: dashboard.open_cases.toString() },
              { label: 'Unread Messages', value: dashboard.unread_messages.toString() },
              { label: 'Return Rate', value: `${dashboard.return_rate_pct.toFixed(1)}%` },
              { label: 'Claim Success', value: `${dashboard.claim_success_rate_pct.toFixed(1)}%` },
              { label: 'Recovery', value: `$${dashboard.recovery_amount.toFixed(2)}` },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'returns' && (
        <div className="space-y-3">
          {returns.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{r.return_id}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(r.status)}`}>{r.status.replace(/_/g, ' ')}</span>
                {r.sku && <span className="text-xs text-slate-500">{r.sku}</span>}
              </div>
              <p className="mt-1 text-sm text-slate-600">{r.reason_detail ?? r.reason?.replace(/_/g, ' ')}</p>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                {r.amazon_order_id && <span>Order: {r.amazon_order_id}</span>}
                {r.refund_amount != null && <span>${r.refund_amount.toFixed(2)}</span>}
                {r.quality_risk != null && r.quality_risk > 0.5 && <span className="text-red-600">Quality risk: {(r.quality_risk * 100).toFixed(0)}%</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'refunds' && (
        <div className="space-y-3">
          {refunds.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{f.refund_id}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(f.status)}`}>{f.status}</span>
                {f.is_partial && <span className="text-xs text-amber-600">Partial</span>}
              </div>
              <div className="mt-2 text-sm">${f.amount.toFixed(2)} {f.currency} · {f.reason}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'safet' && (
        <div className="space-y-3">
          {safeT.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{c.claim_id}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(c.status)}`}>{c.status.replace(/_/g, ' ')}</span>
                <span className="text-xs text-slate-400">{c.claim_type?.replace(/_/g, ' ')}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{c.description}</p>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                <span>Claim: ${c.claim_amount.toFixed(2)}</span>
                {c.recovered_amount != null && c.recovered_amount > 0 && <span className="text-emerald-600">Recovered: ${c.recovered_amount.toFixed(2)}</span>}
                <span>{c.evidence_count} evidence items</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'messages' && (
        <div className="space-y-3">
          {messages.map((m) => (
            <div key={m.id} className={`rounded-xl border p-4 shadow-sm ${m.is_unread ? 'border-sky-200 bg-sky-50/30' : 'border-slate-200 bg-white'}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-sm">{m.subject}</span>
                {m.is_unread && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-700">Unread</span>}
                {m.has_draft && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs text-violet-700">AI Draft</span>}
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${sentimentClass(m.sentiment)}`}>{m.sentiment}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600 line-clamp-2">{m.body}</p>
              <div className="mt-2 text-xs text-slate-500">{m.amazon_order_id} · Priority: {m.priority}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'cases' && (
        <div className="space-y-3">
          {cases.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{c.case_id}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(c.status)}`}>{c.status.replace(/_/g, ' ')}</span>
                {c.sla_status === 'breached' && <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">SLA Breached</span>}
              </div>
              <p className="mt-1 text-sm font-medium">{c.title}</p>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                {c.assignee && <span>{c.assignee}</span>}
                <span>{c.category}</span>
                <span>Priority: {c.priority}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'reimbursements' && (
        <div className="space-y-3">
          {reimbursements.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{r.claim_id}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(r.status ?? 'pending')}`}>{r.status}</span>
                <span className="text-xs text-slate-400">{r.type?.replace(/_/g, ' ')}</span>
              </div>
              <div className="mt-2 text-sm">{r.sku} · {r.quantity} units · ${r.claim_amount.toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'quality' && (
        <div className="space-y-3">
          {quality.map((q) => (
            <div key={q.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{q.sku}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(q.severity)}`}>{q.severity}</span>
                <span className="text-xs text-slate-400">{q.event_type?.replace(/_/g, ' ')}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{q.description}</p>
              <div className="mt-2 text-xs text-slate-500">{q.occurrences} occurrences · Trend: {q.trend}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">#{r.priority_rank}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                {r.sku && <span>SKU: {r.sku}</span>}
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
